import React, { useState } from 'react';
import styled from 'styled-components';

import { useAnchoredPanel } from './useAnchoredPanel';

/**
 * 店長端的日別明細＋逐格覆寫。
 *
 * ⚠️ 只有店長端（劃假彙整）會用到。員工端 ⛔ 永遠看不到例假／休息日這一層，
 *    也 ⛔ 不會載入這個元件的任何互動 —— 員工只標「希望排休／具名假別」。
 *
 * ⛔ 三件事在這裡是硬規則，⛔ 不可以做成可關掉的選項：
 *   1. 例假日 ⛔ 不得改排上班（按鈕直接不給按，並說明為什麼）
 *   2. 具名假別 ⛔ 不得轉成例假或休息日（整組按鈕都關掉，要改請走請假流程）
 *   3. 理由欄顯示但 ⛔ 非必填 —— 稽核留的是操作者／時間／原建議值／覆寫值
 */
const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1040;
    background: transparent;
`;

const Panel = styled.div`
    position: fixed;
    z-index: 1050;
    width: 320px;
    background: #fff;
    border: 1px solid #cfd6dd;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(31, 41, 51, 0.16);
    padding: 11px 12px;
    font-family: 'Noto Sans TC', sans-serif;

    .dt-title {
        font-size: 13px;
        font-weight: bold;
        color: #1f2933;
    }

    .dt-sub {
        font-size: 11px;
        color: #7b8794;
        margin-bottom: 7px;
        padding-bottom: 7px;
        border-bottom: 1px solid #eef1f4;
    }

    .dt-now {
        font-size: 12px;
        color: #1f2933;
        margin-bottom: 4px;
    }

    .dt-reason {
        font-size: 11px;
        line-height: 1.5;
        color: #4b5563;
        background: #f8f9fa;
        border-radius: 4px;
        padding: 5px 7px;
        margin-bottom: 7px;
        max-height: 150px;
        overflow: auto;
    }

    .dt-row {
        display: flex;
        gap: 5px;
        margin-bottom: 6px;
    }

    .dt-note {
        font-size: 10.5px;
        color: #8a5200;
        background: #fff7ec;
        border-radius: 4px;
        padding: 5px 7px;
        margin-bottom: 6px;
        line-height: 1.5;
    }

    input[type='text'] {
        width: 100%;
        font-size: 12px;
        padding: 5px 7px;
        border: 1px solid #cfd6dd;
        border-radius: 4px;
        margin-bottom: 6px;
    }
`;

const TypeButton = styled.button`
    flex: 1;
    border: 1px solid ${(props) => (props.selected ? props.accent : '#cfd6dd')};
    background: ${(props) => (props.selected ? props.accent : '#fff')};
    color: ${(props) => (props.selected ? '#fff' : '#4b5563')};
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 0;
    cursor: pointer;

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const MinorButton = styled.button`
    width: 100%;
    margin-top: 4px;
    border: 1px solid ${(props) => props.tone || '#cfd6dd'};
    background: #fff;
    color: ${(props) => props.tone || '#4b5563'};
    border-radius: 4px;
    font-size: 11.5px;
    font-weight: 600;
    padding: 5px 0;
    cursor: pointer;

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const ACCENT = {
    work: '#607d8b',
    rest_day: '#ff9800',
    regular_holiday: '#f44336',
};

const DayTypePicker = ({
    anchorSelector,
    target,
    options,
    busy,
    onOverride,
    onRevert,
    onHrReview,
    onClose,
}) => {
    const { panelRef, pos } = useAnchoredPanel(anchorSelector, onClose);
    const [reason, setReason] = useState('');

    if (!anchorSelector || !target) return null;

    const { member, date, cell } = target;
    const current = (cell || {}).day_type || null;
    const isNamed = current === 'named_leave';
    const isHoliday = current === 'regular_holiday';
    const isHalf = cell && cell.half_day && cell.half_day !== 'full';
    const hr = (cell || {}).hr_review;

    return (
        <React.Fragment>
            <Backdrop onClick={onClose} />
            <Panel ref={panelRef} style={pos} data-testid="day-type-picker">
                <div className="dt-title">
                    {member.name}　{date.date}（{date.weekday}）
                </div>
                <div className="dt-sub">
                    {cell
                        ? `${cell.is_manual ? '店長調整' : '系統建議'}${
                              cell.decided_by ? ` · ${cell.decided_by}` : ''
                          }${cell.decided_at ? ` · ${cell.decided_at}` : ''}`
                        : '這一格還沒有日別建議'}
                </div>

                {cell ? (
                    <React.Fragment>
                        <div className="dt-now">
                            目前日別：<strong style={{ color: cell.color }}>{cell.label}</strong>
                            {isHalf ? `・${cell.half_day === 'am' ? '上半天' : '下半天'}` : ''}
                            {cell.is_manual && cell.suggested_label
                                ? `（原系統建議：${cell.suggested_label}）`
                                : ''}
                        </div>
                        <div className="dt-reason">
                            <div>{cell.effect}</div>
                            {cell.reason && <div style={{ marginTop: '4px' }}>{cell.reason}</div>}
                        </div>
                    </React.Fragment>
                ) : (
                    <div className="dt-reason">
                        還沒有做過例假／休息日分類。請先按表格上方的「重新分類例假／休息日」。
                    </div>
                )}

                {hr && (
                    <div className="dt-note" data-hr-review>
                        <strong>待人資人工處理</strong>（{hr.marked_by_name} · {hr.marked_at}）
                        {hr.note ? `：${hr.note}` : ''}
                        <br />
                        系統 ⛔ 不自動補休、⛔ 不自行判定法定例外是否成立。
                    </div>
                )}

                {isNamed && (
                    <div className="dt-note">
                        這天是具名假別（特休等），⛔ 不得轉成例假或休息日，也不會因此消耗額度。
                        要改請走請假流程。
                    </div>
                )}

                {isHalf && !isNamed && (
                    <div className="dt-note">
                        這天是半天休，那天仍然要上班 —— ⛔ 半天不能算成完整的例假日或休息日。
                    </div>
                )}

                {isHoliday && (
                    <div className="dt-note">
                        例假日 ⛔ 不得改排上班，這一條沒有覆寫選項。真的遇到天災、事變或突發事件，
                        請按下方「標記待人資人工處理」交由人資個案處理。
                    </div>
                )}

                {cell && !isNamed && !isHalf && (
                    <React.Fragment>
                        <div className="dt-row">
                            {(options || []).map((opt) => {
                                // ⛔ 例假日 → 上班：按鈕直接不給按（上面已說明原因）
                                const blocked = isHoliday && opt.value === 'work';

                                return (
                                    <TypeButton
                                        key={opt.value}
                                        type="button"
                                        data-day-type={opt.value}
                                        accent={ACCENT[opt.value] || '#607d8b'}
                                        selected={current === opt.value}
                                        title={blocked ? '例假日不得改排上班' : opt.effect}
                                        disabled={busy || blocked || current === opt.value}
                                        onClick={() => onOverride(member, date, opt.value, reason)}
                                    >
                                        {opt.label}
                                    </TypeButton>
                                );
                            })}
                        </div>

                        <input
                            type="text"
                            value={reason}
                            maxLength={255}
                            placeholder="覆寫理由（非必填）"
                            data-day-type-reason
                            onChange={(e) => setReason(e.target.value)}
                        />

                        {cell.is_manual && (
                            <MinorButton
                                type="button"
                                data-action="revert-day-type"
                                disabled={busy}
                                onClick={() => onRevert(member, date)}
                            >
                                回復系統建議{cell.suggested_label ? `（${cell.suggested_label}）` : ''}
                            </MinorButton>
                        )}

                        <MinorButton
                            type="button"
                            tone="#8a5200"
                            data-action="hr-review"
                            disabled={busy}
                            onClick={() => onHrReview(member, date, reason)}
                        >
                            標記待人資人工處理（不改日別）
                        </MinorButton>
                    </React.Fragment>
                )}
            </Panel>
        </React.Fragment>
    );
};

export default DayTypePicker;
